import { useCallback } from "react"
import { Capacitor, CapacitorHttp, type HttpHeaders, type HttpOptions } from "@capacitor/core"
import { toast } from "sonner"
import { useAuth } from '@/modules/shared/components/auth-provider'
import { URL, USR } from '@/modules/shared/lib/consts'
import { safeJsonParse } from '@/modules/shared/lib/utils'
import { appendAppVersionHeaders } from '@/modules/shared/lib/app-version'

// ============================================================================
// 🛡️ PROTEÇÃO DE CONCORRÊNCIA / RACE CONDITION
// Contadores GLOBAIS atômicos (fora do React hook = mesmo para TODAS as telas/hooks)
// ============================================================================
const globalPendingRequests: { value: number } = { value: 0 };
const globalLastIdentityChangedAtMs: { value: number } = { value: 0 };

export const isAnyRequestPendingGlobal = (): boolean => globalPendingRequests.value > 0;
export const pendingRequestsCountGlobal = (): number => globalPendingRequests.value;
export const markIdentityChangedGlobal = (): void => {
  globalLastIdentityChangedAtMs.value = Date.now();
};
export const didIdentityChangeRecentlyGlobal = (withinMs = 3000): boolean =>
  (Date.now() - globalLastIdentityChangedAtMs.value) < withinMs;

// ============================================================================
// Fim proteção
// ============================================================================

const _send = async (options: HttpOptions) => {
  if (Capacitor.isNativePlatform()) return await CapacitorHttp.request(options)
  try {
    const response = await fetch(options.url, {
      method: options.method,
      headers: options.headers,
      body: options.data
    })
    if (response.status === 401) return { status: 401, data: null }
    try {
      return { status: response.status, data: await response.json() }
    } catch {
      return { status: response.status, data: null }
    }
  } catch (err) {
    const mensagem = String((err as any)?.message ?? '').toLowerCase()
    const ehAbortado =
      (err instanceof DOMException && err.name === 'AbortError') ||
      (err instanceof TypeError && (/failed to fetch/.test(mensagem) || /networkerror/.test(mensagem)))
    if (ehAbortado) {
      return { status: 0, data: null, aborted: true }
    }
    throw err
  }
}

const ADMIN_PROCS_SKIP_X_SCHOOL_ID = new Set([
  'obter_escolas',
  'obter_escolas_select',
  'persistir_escola',
  'obter_admins',
  'persistir_admin',
  'obter_auditoria',
  'obter_emails_escola'
])

type RequestOptions = {
  skipSuperSchoolId?: boolean
}

const useRequest = () => {
  const { logout } = useAuth();

  const request = useCallback(async (resource: string, method?: 'GET' | 'POST', body?: unknown, options?: RequestOptions): Promise<IResponse> => {
    // 🛡️ Contador global: INCREMENTA antes de tudo
    globalPendingRequests.value++;
    try {
      try {
        // 🛡️ Early exit: checagem troca de identidade RECENTE (< 3s) FEITA ANTES do fetch.
        // Sem isso, o fetch acontecia, era retornado do servidor, e nós o descartávamos
        // no pós-fetch (checagem abaixo). Agora economizamos rede e evitamos ruído em dev.
        // Caso esteja dentro da janela, retornamos exatamente o mesmo resultado do pós-fetch
        // (success=false + err) para manter compatibilidade e o caller retentar.
        if (didIdentityChangeRecentlyGlobal(3000)) {
          return {
            success: false,
            err: new Error('Request descartado após troca de identidade')
          }
        }

        const usr = localStorage.getItem(USR)
        if (!usr) {
          logout?.()
          return { success: false, err: new Error('Não autorizado') }
        }

        const parsedUsr = safeJsonParse<any>(usr)
        const token = parsedUsr?.token
        const headers: HttpHeaders = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }

        const userRole = parsedUsr?.role
        if (userRole && String(userRole).trim() !== '') {
          headers['X-User-Role'] = String(userRole)
        }

        const skipViaOptions = options?.skipSuperSchoolId === true
        const matchProc = resource.match(/^\/proc\/([a-zA-Z0-9_]+)/)
        const procName = matchProc?.[1]?.toLowerCase()
        const skipViaAdminProc = !!procName && ADMIN_PROCS_SKIP_X_SCHOOL_ID.has(procName)
        const skipViaAdminPath = resource.startsWith('/admin/')

        const schoolId = parsedUsr?.school_id
        const isSuper = parsedUsr?.role === 'SUPER'
        if (!skipViaOptions && !skipViaAdminProc && !skipViaAdminPath && isSuper && schoolId !== undefined && schoolId !== null && String(schoolId).trim() !== '') {
          headers['X-School-Id'] = String(schoolId)
        }

        const headersComVersao = appendAppVersionHeaders(headers);

        const response = await _send({
          url: `${URL}${resource}`,
          method: method ?? 'GET',
          headers: headersComVersao,
          data: JSON.stringify(body) ?? null
        })

        const resumoAny = response as any
        const foiAbortado = Boolean(resumoAny?.aborted)
        // 🛡️ Segunda checagem (após fetch): cobre casos em que troca de identidade
        // aconteceu ENQUANTO esperava a resposta.
        const houveTrocaDeIdentidadeRecente = didIdentityChangeRecentlyGlobal(3000)

        if (response.status === 401) {
          toast.error('Sessão expirada. Faça login novamente.')
          logout?.()
          return { success: false, err: new Error('Não autorizado') }
        }

        if (foiAbortado || houveTrocaDeIdentidadeRecente) {
          return {
            success: false,
            err: new Error(foiAbortado ? 'Request abortado' : 'Request descartado após troca de identidade')
          }
        }

        return { success: true, data: response.data }
      } catch (err) {
        const abortOuTrocaRecente =
          (err instanceof DOMException && err.name === 'AbortError') ||
          didIdentityChangeRecentlyGlobal(3000)
        if (abortOuTrocaRecente) {
          return { success: false, err }
        }
        toast.error('Houve uma falha na comunicação com o servidor.')
        return { success: false, err }
      }
    } finally {
      // 🛡️ Contador global: DECREMENTA SEMPRE (mesmo return/throw/erro/exception)
      if (globalPendingRequests.value > 0) globalPendingRequests.value--;
    }
  }, [logout]);

  const get = useCallback(async (resource: string, options?: RequestOptions) => request(resource, 'GET', undefined, options), [request])
  const post = useCallback(async (resource: string, body: object, options?: RequestOptions) => request(resource, 'POST', body, options), [request])

  return { get, post }
};

export default useRequest
